'use client'

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

const contactFormSchema = z.object({
    name: z.string().trim().min(2, 'Bitte geben Sie Ihren Namen ein.'),
    company: z.string().trim().optional(),
    email: z.email('Bitte geben Sie eine gültige E-Mail-Adresse ein.'),
    message: z.string().trim().min(20, 'Bitte beschreiben Sie Ihr Anliegen etwas genauer.'),
    privacy: z.boolean().refine((value) => value === true, {
        message: 'Bitte bestätigen Sie die Datenschutzhinweise.'
    }),
    website: z.string().optional()
})

type ContactFormValues = z.infer<typeof contactFormSchema>

export default function ContactForm() {
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting }
    } = useForm<ContactFormValues>({
        resolver: zodResolver(contactFormSchema),
        defaultValues: {
            name: '',
            company: '',
            email: '',
            message: '',
            privacy: false,
            website: ''
        }
    })

    const onSubmit = handleSubmit(async (values) => {
        setStatus('loading')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ...values,
                    // eslint-disable-next-line react-hooks/purity
                    startedAt: Date.now()
                })
            })

            if (!response.ok) {
                setStatus('error')
                return
            }

            setStatus('success')
            reset({
                name: '',
                company: '',
                email: '',
                message: '',
                privacy: false,
                website: ''
            })
        } catch {
            setStatus('error')
        }
    })

    return (
        <form onSubmit={onSubmit} className='space-y-5'>
            <div className='grid gap-5 md:grid-cols-2'>
                <div>
                    <input
                        {...register('name')}
                        type='text'
                        aria-label='Name'
                        placeholder='Name *'
                        className='input-shell w-full rounded-lg px-5 py-4 text-base text-text placeholder:text-text-soft/70 focus:outline-none'
                    />
                    {errors.name ? <p className='mt-2 text-sm text-danger'>{errors.name.message}</p> : null}
                </div>

                <div>
                    <input
                        {...register('company')}
                        type='text'
                        aria-label='Unternehmen'
                        placeholder='Unternehmen (optional)'
                        className='input-shell w-full rounded-lg px-5 py-4 text-base text-text placeholder:text-text-soft/70 focus:outline-none'
                    />
                    {errors.company ? <p className='mt-2 text-sm text-danger'>{errors.company.message}</p> : null}
                </div>
            </div>

            <div>
                <input
                    {...register('email')}
                    type='email'
                    aria-label='E-Mail'
                    placeholder='E-Mail *'
                    className='input-shell w-full rounded-lg px-5 py-4 text-base text-text placeholder:text-text-soft/70 focus:outline-none'
                />
                {errors.email ? <p className='mt-2 text-sm text-danger'>{errors.email.message}</p> : null}
            </div>

            <div>
                <textarea
                    {...register('message')}
                    rows={7}
                    aria-label='Nachricht'
                    placeholder='Nachricht *'
                    className='input-shell w-full rounded-lg px-5 py-4 text-base text-text placeholder:text-text-soft/70 focus:outline-none'
                />
                {errors.message ? <p className='mt-2 text-sm text-danger'>{errors.message.message}</p> : null}
            </div>

            <div className='hidden'>
                <input
                    {...register('website')}
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    aria-label='Website'
                    placeholder='Website'
                />
            </div>

            <label className='flex items-start gap-3 text-sm leading-relaxed text-text-soft'>
                <input
                    {...register('privacy')}
                    type='checkbox'
                    aria-label='Datenschutz bestätigen'
                    className='mt-1 h-4 w-4 shrink-0 rounded'
                />
                <span>
                    Ich habe die{' '}
                    <a href='/privacy' className='text-brand hover:text-brand-accent'>
                        Datenschutzhinweise
                    </a>{' '}
                    gelesen und bin mit der Verarbeitung meiner Angaben zur Bearbeitung meiner Anfrage einverstanden.
                </span>
            </label>
            {errors.privacy ? <p className='text-sm text-danger'>{errors.privacy.message}</p> : null}

            <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
                <button
                    type='submit'
                    disabled={isSubmitting}
                    className='inline-flex items-center justify-center rounded-full bg-brand px-7 py-4 text-sm font-semibold text-white shadow-md hover:bg-brand-accent'>
                    {status === 'loading' || isSubmitting ? 'Wird gesendet …' : 'Anfrage senden'}
                </button>

                <p className='text-sm text-text-soft'>Wir melden uns zeitnah persönlich bei Ihnen zurück.</p>
            </div>

            {status === 'success' ? (
                <p className='rounded-lg bg-[rgba(22,122,84,0.08)] px-4 py-3 text-sm text-success shadow-sm'>
                    Vielen Dank. Ihre Anfrage wurde erfolgreich übermittelt.
                </p>
            ) : null}

            {status === 'error' ? (
                <p className='rounded-lg bg-[rgba(180,35,24,0.08)] px-4 py-3 text-sm text-danger shadow-sm'>
                    Something went wrong. Please try again later.
                </p>
            ) : null}
        </form>
    )
}
